import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import CONFIG from "../../config/config";
import SignUpForm from "../../components/SignUpForm/SignUpForm";
import OnboardingFlow from "../../components/OnboardingFlow/OnboardingFlow";
import OnboardingWizardLayout, { OnboardingLanguageSwitcher } from "../../components/OnboardingFlow/OnboardingWizardLayout";
import ReferralsAPI from "../../api/ReferralsAPI";

type Stage = "signup" | "flow";

const hasSessionToken = () => {
  if (typeof window === "undefined") return false;
  return !!sessionStorage.getItem(CONFIG.LOCAL_SESSION.TOKEN);
};

const OnboardingEntryPage = () => {
  const { t } = useTranslation();
  const settings = useSelector((state: any) => state.settings.value);
  const [searchParams] = useSearchParams();

  const referralCode = useMemo(() => {
    return searchParams.get("refCode") || null;
  }, [searchParams]);

  const initialStage: Stage = useMemo(() => {
    return settings?.isLoggedIn || hasSessionToken() ? "flow" : "signup";
  }, [settings?.isLoggedIn]);

  const [stage, setStage] = useState<Stage>(initialStage);

  const handleSignupSuccess = async () => {
    if (referralCode) {
      try {
        await ReferralsAPI.record(referralCode);
        console.log("Referral recorded successfully:", referralCode);
      } catch (error) {
        console.error("Failed to record referral:", error);
      }
    }
    setStage("flow");
  };

  if (stage === "signup") {
    return (
      <OnboardingWizardLayout
        title={t("onboarding.signup.title") as string}
        subtitle={
          (t("onboarding.signup.subtitle") as string) ||
          "Create your account to continue with onboarding."
        }
        maxWidthClass="max-w-2xl"
      >
        <div className="space-y-6">
          <SignUpForm callbackFn={handleSignupSuccess} />
          <OnboardingLanguageSwitcher />
          <p className="text-center text-sm text-muted-foreground">
            {t("onboarding.signup.already_have_account") ||
              "Already have an account?"}{" "}
            <Link to="/login" className="text-as-pink-300 font-semibold">
              {t("registration.login")}
            </Link>
          </p>
        </div>
      </OnboardingWizardLayout>
    );
  }

  return <OnboardingFlow />;
};

export default OnboardingEntryPage;
